import type {
	IComunicacaoNotificacaoItem,
	IComunicacaoNotificacaoMetadata,
	IStatusComunicacaoTipo,
} from "@/api/space/dtos/comunication.dto";
import { parseNonEmptyString } from "@/contexts/websocket/parsers";
import type {
	WebSocketAnaliseEstoqueDownloadExcelResponse,
	WebSocketNotificationResponse,
} from "@/contexts/websocket/types";
import { parseDataHoraUTC } from "@/utils/validators/date";
import type { QuickPanelMessage } from "../quickPanel.types";

// --------------------------------- TYPES ---------------------------------
type QuickPanelCallbackDestination = { type: "internal"; url: string } | { type: "external"; url: string } | null;

// -------------------------------- HELPERS --------------------------------
function createQuickPanelMessageId(index: number): string {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();

	const randomPart = (() => {
		if (typeof globalThis !== "undefined" && globalThis.crypto && "getRandomValues" in globalThis.crypto) {
			const randomBytes = new Uint32Array(2);
			globalThis.crypto.getRandomValues(randomBytes);
			return Array.from(randomBytes)
				.map((value) => value.toString(36))
				.join("");
		}

		return `${Date.now().toString(36)}-${index.toString(36)}`;
	})();

	return `websocket-${Date.now()}-${index}-${randomPart}`;
}

function parseNotificationDate(value: unknown): Date | null {
	return parseDataHoraUTC(value);
}

function parseNotificationMetadata(value: unknown): IComunicacaoNotificacaoMetadata {
	if (Array.isArray(value)) return value;
	if (typeof value === "object" && value !== null) return value as Record<string, unknown>;
	return [];
}

function parseNotificationCallbackUrl(value: unknown): string | null {
	return parseNonEmptyString(value) || null;
}

function createQuickPanelRedirectAction(callbackUrl: string | null): Pick<QuickPanelMessage, "action"> {
	return callbackUrl
		? {
				action: {
					type: "redirect",
					label: "Abrir",
					payload: { url: callbackUrl },
				},
			}
		: {};
}

export function resolveQuickPanelCallbackUrl(
	callbackUrl: unknown,
	currentOrigin: string,
): QuickPanelCallbackDestination {
	const rawUrl = parseNonEmptyString(callbackUrl);
	if (!rawUrl) return null;

	try {
		const parsedUrl = new URL(rawUrl, `${currentOrigin}/`);
		if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") return null;

		if (parsedUrl.origin === currentOrigin)
			return {
				type: "internal",
				url: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
			};

		return { type: "external", url: parsedUrl.toString() };
	} catch {
		return null;
	}
}

// ------------------------------- FUNCTIONS -------------------------------
export function toQuickPanelApiMessage(notificacao: IComunicacaoNotificacaoItem): QuickPanelMessage {
	const callbackUrl = parseNotificationCallbackUrl(notificacao.callback_url);

	return {
		id: notificacao.id,
		origin: "api",
		variant: notificacao.tipo,
		titulo: notificacao.titulo,
		mensagem: notificacao.mensagem,
		enviado_em: parseNotificationDate(notificacao.criada_em),
		metadata: parseNotificationMetadata(notificacao.metadata),
		callback_url: callbackUrl,
		lido: notificacao.lida,
		lida_em: parseNotificationDate(notificacao.lida_em),
		...createQuickPanelRedirectAction(callbackUrl),
	};
}

/**
 * Retorna um parser para eventos `notification:*` usados no snackbar e no
 * contador. A listagem persistente continua vindo da API.
 */
export function toQuickPanelMessage(
	variant: IStatusComunicacaoTipo,
): (response: unknown, index: number) => QuickPanelMessage | null {
	return (response, index) => {
		if (typeof response !== "object" || response === null || Array.isArray(response)) return null;

		const data = response as Partial<WebSocketNotificationResponse> & Record<string, unknown>;
		const titulo = parseNonEmptyString(data.titulo);
		const mensagem = parseNonEmptyString(data.mensagem);
		const callbackUrl = parseNotificationCallbackUrl(data.callback_url);

		if (!titulo || !mensagem) return null;

		return {
			id: createQuickPanelMessageId(index),
			origin: "websocket",
			variant,
			titulo,
			mensagem,
			enviado_em: parseNotificationDate(data.enviado_em),
			metadata: parseNotificationMetadata(data.metadata),
			callback_url: callbackUrl,
			lido: false,
			lida_em: null,
			...createQuickPanelRedirectAction(callbackUrl),
		};
	};
}

/**
 * Retorna um parser para eventos `analise_estoque_excel:pronto`.
 * A mensagem inclui uma ação de download para a UI renderizar o botão.
 */
export function toAnaliseEstoqueExcelMessage(response: unknown, index: number): QuickPanelMessage | null {
	if (typeof response !== "object" || response === null || Array.isArray(response)) return null;

	const data = response as Partial<WebSocketAnaliseEstoqueDownloadExcelResponse> & Record<string, unknown>;
	const titulo = parseNonEmptyString(data.titulo);
	const mensagem = parseNonEmptyString(data.mensagem);
	const idRelatorio = parseNonEmptyString(data.id_relatorio);
	const idArquivo = parseNonEmptyString(data.id_arquivo);
	const enviadoEm = parseNotificationDate(data.enviado_em);

	if (!titulo || !mensagem || !idRelatorio || !idArquivo || !enviadoEm) return null;

	return {
		id: parseNonEmptyString(data.id) || createQuickPanelMessageId(index),
		origin: "websocket",
		variant: "info",
		titulo,
		mensagem,
		enviado_em: enviadoEm,
		metadata: { id_relatorio: idRelatorio, id_arquivo: idArquivo },
		callback_url: null,
		lido: false,
		lida_em: null,
		action: {
			type: "download",
			label: "Baixar",
			payload: { id_relatorio: idRelatorio, id_arquivo: idArquivo },
		},
	};
}
